import { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Container, Input, Label } from "reactstrap";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { getShortLink, updateShortLink, fetchMerchants } from "../../utils/shortLinkCalls";
import { errorToast, successToast } from "../../shared/Toast";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Tile, Badge, Loader, GradientDefs, ChartTooltip } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); successToast("Copied to clipboard"); }
  catch { errorToast("Copy failed — please copy manually"); }
};

const inputStyle = { borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 14, fontSize: 13 };
const ghostBtn = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 12, fontWeight: 700 };

const deviceLabel = { android: "Android", ios: "iOS", desktop: "Desktop", other: "Other" };

const EditShortLink = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [link, setLink] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [form, setForm] = useState({ sourceType: "url", targetUrl: "", releaseRepo: "", releaseAssetName: "", label: "", merchantReference: "", active: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [data, ms] = await Promise.all([getShortLink(id), fetchMerchants()]);
      setMerchants(ms);
      if (data) {
        setLink(data);
        setForm({
          sourceType: data.sourceType || "url",
          targetUrl: data.targetUrl || "",
          releaseRepo: data.releaseRepo || "",
          releaseAssetName: data.releaseAssetName || "",
          label: data.label || "",
          merchantReference: data.merchantReference || "",
          active: !!data.active,
        });
      }
      setLoading(false);
    })();
  }, [id]);

  const handleSave = async () => {
    const payload = {
      label: form.label.trim() || null,
      merchantReference: form.merchantReference || null,
      active: form.active,
    };
    if (form.sourceType === "github_latest") {
      if (!/^[\w.-]+\/[\w.-]+$/.test(form.releaseRepo.trim())) {
        errorToast('Enter the GitHub repo as "owner/name"');
        return;
      }
      payload.releaseRepo = form.releaseRepo.trim();
      payload.releaseAssetName = form.releaseAssetName.trim() || null;
    } else {
      if (!/^https?:\/\/.+/i.test(form.targetUrl.trim())) {
        errorToast("Enter a valid URL starting with http:// or https://");
        return;
      }
      payload.targetUrl = form.targetUrl.trim();
    }
    setSaving(true);
    const updated = await updateShortLink(id, payload);
    setSaving(false);
    if (updated) setLink((prev) => ({ ...prev, ...updated }));
  };

  if (loading) return (<Fragment><style>{reportingGlobalCSS}</style><Container fluid className="rpt-wrap"><Loader /></Container></Fragment>);
  if (!link) return (<Fragment><style>{reportingGlobalCSS}</style><Container fluid className="rpt-wrap"><p style={{ padding: 40 }}>Short link not found.</p></Container></Fragment>);

  const a = link.analytics || { totalViews: 0, totalDownloads: 0, timeSeries: [], deviceBreakdown: [] };
  const maxDevice = Math.max(1, ...a.deviceBreakdown.map((d) => d.count));

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title={link.label || `/s/${link.code}`} parent="App Links" />
      <Container fluid className="rpt-wrap">
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* Edit form */}
          <RCard title="Link settings" style={{ flex: "1 1 380px", minWidth: 320 }}>
            {form.sourceType === "github_latest" ? (
              <>
                <div style={{ marginBottom: 12 }}><Badge color={C.blue} bg={C.blueLight}>↻ Latest GitHub release</Badge></div>
                <Label>GitHub repo *</Label>
                <Input type="text" value={form.releaseRepo} onChange={(e) => setForm((p) => ({ ...p, releaseRepo: e.target.value }))} style={{ ...inputStyle, fontFamily: "monospace" }} placeholder="owner/name" />
                <Label>Asset file name (optional)</Label>
                <Input type="text" value={form.releaseAssetName} onChange={(e) => setForm((p) => ({ ...p, releaseAssetName: e.target.value }))} style={{ ...inputStyle, fontFamily: "monospace" }} placeholder="e.g. wags-pos.apk — blank = first .apk" />
                <div style={{ fontSize: 11.5, color: C.textLight, marginTop: -8, marginBottom: 14 }}>
                  Resolves to the newest release each time it&apos;s opened — the link &amp; QR never change when you publish a new build.
                </div>
              </>
            ) : (
              <>
                <Label>Target URL *</Label>
                <Input type="text" value={form.targetUrl} onChange={(e) => setForm((p) => ({ ...p, targetUrl: e.target.value }))} style={inputStyle} />
                <div style={{ fontSize: 11.5, color: C.textLight, marginTop: -8, marginBottom: 14 }}>
                  Changing this keeps the same short link &amp; QR — clients don&apos;t need a new one.
                </div>
              </>
            )}

            <Label>Label</Label>
            <Input type="text" value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} placeholder="e.g. Wags POS v2.3.1" style={inputStyle} />

            <Label>Merchant</Label>
            <Input type="select" value={form.merchantReference} onChange={(e) => setForm((p) => ({ ...p, merchantReference: e.target.value }))} style={inputStyle}>
              <option value="">— None —</option>
              {merchants.map((m) => <option key={m.id} value={m.reference}>{m.name} ({m.reference})</option>)}
            </Input>

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 18px" }}>
              <Input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} style={{ marginTop: 0 }} />
              <span style={{ fontSize: 13, color: C.text }}>Active</span>
              <Badge color={form.active ? C.green : C.coral} bg={form.active ? C.greenLight : C.coralLight}>{form.active ? "Enabled" : "Disabled"}</Badge>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <Button disabled={saving} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleSave}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => navigate("/short-links")}>Back to list</Button>
            </div>
          </RCard>

          {/* QR + share */}
          <RCard title="Short link & QR" style={{ flex: "0 0 300px", textAlign: "center" }}>
            <img src={link.qrUrl} alt="QR code" width={200} height={200} style={{ borderRadius: 12, border: `1px solid ${C.border}`, padding: 8, background: "#fff" }} />
            <code style={{ display: "block", fontSize: 13, background: C.bg, padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.borderLight}`, margin: "14px 0", wordBreak: "break-all", fontWeight: 600 }}>{link.shortUrl}</code>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <Button size="sm" style={ghostBtn} onClick={() => copy(link.shortUrl)}>Copy link</Button>
              <a href={link.qrUrl + "?download=1"} download style={{ textDecoration: "none" }}><Button size="sm" style={ghostBtn}>Download QR</Button></a>
              <a href={link.shortUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}><Button size="sm" style={ghostBtn}>Open</Button></a>
            </div>
          </RCard>
        </div>

        {/* Analytics */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, margin: "4px 0 20px" }}>
          <Tile label="Total Opens" value={a.totalViews} icon="👀" />
          <Tile label="Downloads" value={a.totalDownloads} icon="⬇" gradient={`linear-gradient(90deg, ${C.blue}, #A8B8FF)`} />
        </div>

        <RCard title="Opens over time">
          {a.timeSeries.length === 0 ? (
            <p style={{ textAlign: "center", padding: 30, color: C.textLight }}>No activity yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={a.timeSeries} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <GradientDefs />
                <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: C.textMed }} tickLine={false} axisLine={{ stroke: C.border }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.textMed }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="views" name="Opens" stroke="#009689" strokeWidth={2} fill="url(#gCoral)" />
                <Area type="monotone" dataKey="downloads" name="Downloads" stroke="#667EEA" strokeWidth={2} fill="url(#gBlue)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </RCard>

        <RCard title="By device">
          {a.deviceBreakdown.length === 0 ? (
            <p style={{ textAlign: "center", padding: 20, color: C.textLight }}>No activity yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {a.deviceBreakdown.map((d) => (
                <div key={d.deviceType} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 80, fontSize: 13, color: C.textMed }}>{deviceLabel[d.deviceType] || d.deviceType}</span>
                  <div style={{ flex: 1, height: 10, background: C.borderLight, borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ width: `${(d.count / maxDevice) * 100}%`, height: "100%", background: `linear-gradient(90deg, ${C.coral}, #00bfa5)`, borderRadius: 6 }} />
                  </div>
                  <span style={{ width: 40, textAlign: "right", fontSize: 13, fontWeight: 700, color: C.text }}>{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </RCard>
      </Container>
    </Fragment>
  );
};

export default EditShortLink;
